import React, { useEffect, Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { SectionHeading } from "../components/shared/SectionHeading";
import CTABanner from "../components/shared/CTABanner";
import { CASE_STUDIES } from "../lib/data";

const FloatingKnot = lazy(() => import("../components/three/FloatingKnot"));

/**
 * Scroll to a hash-targeted element using rAF retries so it lands
 * correctly even when images/layout push the element after first paint.
 * Retries up to ~2 seconds (120 frames) or until element appears stable.
 */
function scrollToHashWithRetry(id, maxFrames = 120) {
    let frames = 0;
    let lastTop = -1;
    let stableFrames = 0;

    const tick = () => {
        const el = document.getElementById(id);
        if (!el) {
            if (++frames < maxFrames) requestAnimationFrame(tick);
            return;
        }
        const rect = el.getBoundingClientRect();
        const top = rect.top;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        // Once the target's position stops shifting between frames, stop retrying.
        if (Math.abs(top - lastTop) < 1) {
            stableFrames += 1;
        } else {
            stableFrames = 0;
        }
        lastTop = top;
        if (stableFrames < 6 && ++frames < maxFrames) {
            requestAnimationFrame(tick);
        }
    };
    requestAnimationFrame(tick);
}

export default function CaseStudies() {
    useEffect(() => {
        if (window.location.hash) {
            const id = window.location.hash.slice(1);
            scrollToHashWithRetry(id);
        }
    }, []);

    return (
        <main data-testid="page-case-studies" className="relative pt-32 md:pt-40">
            <section className="relative pb-16 overflow-hidden">
                <div className="absolute inset-0 -z-10 bg-radial-fade" />
                <div className="absolute right-0 top-0 -z-10 h-[360px] w-[360px] hidden md:block opacity-55">
                    <Suspense fallback={null}>
                        <FloatingKnot color="#1E88E5" accent="#7FD03A" speed={0.2} />
                    </Suspense>
                </div>
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <SectionHeading
                        eyebrow="Case Studies"
                        title="Evidence over promises."
                        subtitle="Selected transformations engineered by our teams across BFSI, Healthcare, Manufacturing and Government."
                    />
                </div>
            </section>

            <section className="relative py-14">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10 space-y-5">
                    {/* Featured trio: 1 hero + 2 stacked */}
                    {CASE_STUDIES.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-[minmax(280px,1fr)]">
                            {CASE_STUDIES.slice(0, 3).map((cs, i) => {
                                const span =
                                    i === 0
                                        ? "md:col-span-8 md:row-span-2"
                                        : "md:col-span-4";
                                return (
                                    <motion.article
                                        key={cs.id}
                                        id={cs.id}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true, margin: "-80px" }}
                                        transition={{ duration: 0.6, delay: i * 0.06 }}
                                        className={`group relative overflow-hidden rounded-3xl border border-white/10 scroll-mt-28 ${span}`}
                                        data-testid={`case-detail-${cs.id}`}
                                    >
                                        <img
                                            src={cs.image}
                                            alt={cs.title}
                                            className="absolute inset-0 h-full w-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/70 to-transparent" />
                                        <div className="relative h-full flex flex-col justify-end p-7 md:p-9">
                                            <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-brand-green mb-3">
                                                <span className="h-px w-6 bg-gradient-to-r from-brand-blue to-brand-green" />
                                                {cs.industry}
                                            </div>
                                            <h3 className={`font-display ${i === 0 ? "text-2xl md:text-3xl lg:text-4xl" : "text-xl md:text-2xl"} font-semibold tracking-tight leading-tight max-w-2xl`}>
                                                {cs.title}
                                            </h3>
                                            <p className="mt-3 text-zinc-300 text-[14px] max-w-2xl line-clamp-3">
                                                {cs.summary}
                                            </p>
                                            <div className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
                                                {cs.metrics.slice(0, i === 0 ? 3 : 2).map((m) => (
                                                    <div key={m.label}>
                                                        <div className={`font-display ${i === 0 ? "text-2xl md:text-3xl" : "text-xl"} font-bold text-gradient-brand`}>
                                                            {m.value}
                                                        </div>
                                                        <div className="text-[10.5px] text-zinc-400 uppercase tracking-[0.18em]">
                                                            {m.label}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </motion.article>
                                );
                            })}
                        </div>
                    )}

                    {/* Remainder: clean uniform 3-col grid */}
                    {CASE_STUDIES.length > 3 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 pt-2">
                            {CASE_STUDIES.slice(3).map((cs, i) => (
                                <motion.article
                                    key={cs.id}
                                    id={cs.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.55, delay: (i % 3) * 0.05 }}
                                    className="group relative overflow-hidden rounded-3xl border border-white/10 scroll-mt-28 h-[420px]"
                                    data-testid={`case-detail-${cs.id}`}
                                >
                                    <img
                                        src={cs.image}
                                        alt={cs.title}
                                        className="absolute inset-0 h-full w-full object-cover opacity-50 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/75 to-transparent" />
                                    <div className="relative h-full flex flex-col justify-end p-7">
                                        <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-brand-green mb-3">
                                            <span className="h-px w-6 bg-gradient-to-r from-brand-blue to-brand-green" />
                                            {cs.industry}
                                        </div>
                                        <h3 className="font-display text-xl md:text-[22px] font-semibold tracking-tight leading-tight">
                                            {cs.title}
                                        </h3>
                                        <p className="mt-3 text-zinc-300 text-[13.5px] line-clamp-3">
                                            {cs.summary}
                                        </p>
                                        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2.5">
                                            {cs.metrics.slice(0, 2).map((m) => (
                                                <div key={m.label}>
                                                    <div className="font-display text-xl font-bold text-gradient-brand">
                                                        {m.value}
                                                    </div>
                                                    <div className="text-[10px] text-zinc-400 uppercase tracking-[0.18em]">
                                                        {m.label}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <section className="relative py-16 md:py-20">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 lg:px-10">
                    <CTABanner
                        eyebrow="Your case study next"
                        title="Ready to be our next success story?"
                        subtitle="We'll design a pilot that maps to a board level KPI within 90 days."
                    />
                </div>
            </section>
        </main>
    );
}
